import { useEffect, useRef, useState } from 'react';
import {
  GRID_COLS,
  GRID_ROWS,
  SPAN_LEVELS,
  type TileLayout,
} from '@/pages/spatial-span-spick/gameConfig';
import type { RoundFeedback } from '@/pages/spatial-span-spick/hooks/useGameState';
import TileGrid from './TileGrid';

interface GameScreenProps {
  stage: number;
  layout: TileLayout;
  sequence: number[];
  showingIndex: number;
  isShowing: boolean;
  onTilePress: (index: number) => void;
  isInputEnabled: boolean;
  userSequence: number[];
  message: string;
  feedback: RoundFeedback;
  isFinalizing: boolean;
  replayIndex: number;
  isReplaying: boolean;
  stagePulse: boolean;
  stageText: string;
}

// 스픽(Spick) 버전 게임 화면
// - 공통 요소: 연한 파랑 배경, "두뇌풀기 게임" 타이틀 + 진행 배지, 안내 문구(아이콘), 점선 흰 게임보드
export default function GameScreen({
  stage,
  layout,
  sequence,
  showingIndex,
  isShowing,
  onTilePress,
  isInputEnabled,
  userSequence,
  message,
  feedback,
  isFinalizing,
  replayIndex,
  isReplaying,
  stagePulse,
  stageText,
}: GameScreenProps) {
  // 한 번에 하나의 안내만 노출하고, 문구에 맞는 아이콘을 앞에 함께 보여준다
  const guidanceText = isShowing
    ? '불이 켜지는 위치와 순서를 기억해주세요'
    : isInputEnabled
      ? '같은 순서대로 눌러주세요'
      : message;

  const guidanceIcon = isShowing
    ? '💡'
    : isInputEnabled
      ? '👆'
      : feedback === 'correct'
        ? '✅'
        : feedback === 'wrong'
          ? '💪'
          : '💡';

  // 게임판이 실제로 차지한 영역을 측정해 셀에 정확히 들어가는 타일 크기를 계산한다.
  // (브라우저 주소창/WebView 높이 변화, Safe Area 와 무관하게 항상 남은 영역을 최대한 사용)
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [tileSize, setTileSize] = useState(64);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const padding =
        parseFloat(
          style.getPropertyValue('--sh-board-padding') ||
            style.paddingTop ||
            '16',
        ) || 16;

      const cellWidth = (rect.width - padding * 2) / GRID_COLS;
      const cellHeight = (rect.height - padding * 2) / GRID_ROWS;
      const next = Math.floor(Math.min(cellWidth, cellHeight, 88));
      setTileSize(Math.max(30, next));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('orientationchange', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return (
    <div className="relative flex flex-col h-full sh-game-bg pb-[var(--sh-screen-pb)] animate-fade-in-up">
      {/* 게임 단계 노출 영역: 높이 44 전용 레이어 박스 (게임명 + 진행 표시) */}
      <div className="shrink-0 h-[44px] sh-header-bg px-5 flex items-center justify-between">
        <h1 className="text-[14px] font-bold text-[#101828]">두뇌풀기 게임</h1>
        <span
          className="inline-flex items-center h-7 px-3 rounded-full text-white text-[13px] font-bold tabular-nums whitespace-nowrap"
          style={{ backgroundColor: 'var(--sh-stage-badge-bg)' }}
        >
          {stage}/{SPAN_LEVELS.length}
        </span>
      </div>

      {/* 현재 행동 안내: 타이틀(14px)보다 큰 16px, 중앙정렬 */}
      <div className="h-[var(--sh-guidance-height)] shrink-0 flex items-center justify-center px-5">
        <p className="text-[16px] font-bold leading-snug text-center text-[#101828]">
          <span className="mr-1.5" aria-hidden="true">
            {guidanceIcon}
          </span>
          {guidanceText}
        </p>
      </div>

      {/* Play Area: 흰 게임보드 + 점선 그리드 (Stage 성공 시 1회 Pulse) */}
      <div className="flex-1 min-h-0 flex flex-col px-5">
        <div
          className={`
            relative w-full flex-1 min-h-0 rounded-3xl overflow-hidden
            bg-white sh-dot-grid shadow-[0_6px_20px_oklch(var(--primary-500)/0.07)]
            ${stagePulse ? 'animate-board-pulse' : ''}
          `}
          ref={boardRef}
        >
          <TileGrid
            tileSize={tileSize}
            layout={layout}
            sequence={sequence}
            showingIndex={showingIndex}
            isShowing={isShowing}
            onTilePress={onTilePress}
            isInputEnabled={isInputEnabled}
            userSequence={userSequence}
            feedback={feedback}
            replayIndex={replayIndex}
            isReplaying={isReplaying}
          />

          {/* Stage 성공 시 Play Area 위에서 짧게 떠오르는 텍스트 (별도 Modal 아님) */}
          {stageText && (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
              <span className="animate-stage-text text-[22px] font-bold text-primary-600">
                {stageText}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 전체 완료 연출 (짧게, 과한 Effect 없음) */}
      {isFinalizing && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/85">
          <div className="flex flex-col items-center animate-sh-complete">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
              <i className="ri-check-line text-3xl leading-none" />
            </div>
            <p className="text-2xl font-bold text-foreground-950">완료!</p>
          </div>
        </div>
      )}
    </div>
  );
}